import { useCallback, useMemo } from 'react';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { HubOption } from 'dorian-shared/types/branch/HubOption';
import type { TextDestCondition } from 'dorian-shared/types/branch/HubOption';
import { MemoryDTO } from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { BranchSelectItem } from '../shared/types';
import { createDefaultTextDestCondition } from '../hubStepDefaults';
import { getBranchName } from '../shared/hubUtils';

type OnChange = (changes: Partial<HubOption>) => void;

export function useTextDestConditions(
  textDestConditions: TextDestCondition[],
  onChange: OnChange,
  memoryVariables: MemoryDTO[],
  branches: BranchSelectItem[],
) {
  const addCondition = useCallback(() => {
    const newCondition = createDefaultTextDestCondition();
    onChange({ textDestConditions: [...textDestConditions, newCondition] });
  }, [textDestConditions, onChange]);

  const removeCondition = useCallback(
    (uuid: string) => {
      onChange({
        textDestConditions: textDestConditions.filter((c) => c.uuid !== uuid),
      });
    },
    [textDestConditions, onChange],
  );

  const updateCondition = useCallback(
    (uuid: string, updated: TextDestCondition) => {
      onChange({
        textDestConditions: textDestConditions.map((c) =>
          c.uuid === uuid ? updated : c,
        ),
      });
    },
    [textDestConditions, onChange],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = textDestConditions.findIndex((c) => c.uuid === active.id);
        const newIndex = textDestConditions.findIndex((c) => c.uuid === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          onChange({ textDestConditions: arrayMove(textDestConditions, oldIndex, newIndex) });
        }
      }
    },
    [textDestConditions, onChange],
  );

  const summary = useMemo((): string => {
    if (textDestConditions.length === 0) return '';

    const first = textDestConditions[0];
    const parts: string[] = [];

    if (first.resultText != null) {
      parts.push(`"${first.resultText}"`);
    }

    if (first.resultGotoBranchId != null) {
      const name = getBranchName(first.resultGotoBranchId, branches);
      parts.push(`→ ${name || '...'}`);
    }

    let text = parts.join(' ') || 'override';

    if (textDestConditions.length > 1) {
      text += ` + ${textDestConditions.length - 1} more`;
    }

    return text;
  }, [textDestConditions, branches]);

  return {
    addCondition,
    removeCondition,
    updateCondition,
    handleDragEnd,
    summary,
  };
}
